import {
  DeviceMenuLocation,
  ScreenType,
  type Layout,
  type LocationDeviceConfig,
  type Media,
  type Post,
  type PostTileAsset,
  type ProfileLayoutScreenModel,
  type ProfileLayoutScreenPositionModel,
  type ProfileRestingInputEntryImageSettings,
  type Service,
} from "@package/ui-gql/index-data-access";
import type {
  PayloadAction,
  ReducersMapObject,
  Store,
  UnknownAction,
} from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

export type SliceWallMediaType = "image" | "video" | "binary";

export type SliceWallInitialization =
  | "starting"
  | "activating"
  | "activated"
  | "activation-required"
  | "denied"
  | "loading"
  | "running"
  | "failure";

export type LayoutPostPosition = {
  id: string;
  post: Post;
  asset: PostTileAsset;
  relatedPosts: Post[];
};

export type SliceWallScreenState = {
  id: string;
  active: boolean;
  flyout?: {
    position?: ProfileLayoutScreenPositionModel;
    post: Post;
    relatedPosts: Post[];
    staleTimeout?: number;
  };
  index: number;
  layout: Layout;
  layoutPostPositions: LayoutPostPosition[];
  menu: boolean;
  position?: ProfileLayoutScreenPositionModel;
};

export type SliceWallState = {
  background: {
    active: boolean;
    media?: Media;
    audio: boolean;
    loop: boolean;
    type: SliceWallMediaType;
  };
  caching: boolean;
  device: LocationDeviceConfig;
  deviceId?: string;
  initalization: SliceWallInitialization;
  location: string;
  profile: string;
  profileScreen: ProfileLayoutScreenModel;
  resting: {
    active: boolean;
    audio: boolean;
    imageSettings?: ProfileRestingInputEntryImageSettings;
    interrupt: boolean;
    media?: Media;
    transparent: boolean;
    type: SliceWallMediaType;
  };
  screens: SliceWallScreenState[];
  screenMenuLocation: DeviceMenuLocation;
  services: Service[];
  styling: {
    animation: string;
  };
  type: ScreenType;
  videoSource?: string;
};

const initialState: SliceWallState = {
  background: {
    active: false,
    audio: false,
    loop: false,
    type: "image",
  },
  caching: false,
  device: {},
  initalization: "starting",
  location: "",
  profile: "",
  profileScreen: {},
  styling: {
    animation: "",
  },
  resting: {
    active: false,
    audio: false,
    interrupt: true,
    transparent: true,
    type: "image",
  },
  screens: [],
  screenMenuLocation: DeviceMenuLocation.None,
  services: [],
  type: ScreenType.Tv,
};

const slice = createSlice({
  name: "wall",
  initialState,
  reducers: {
    sliceWallResetState: (draft) => {
      Object.assign(draft, initialState);
    },
    sliceWallSetInitialization: (
      draft,
      action: PayloadAction<SliceWallInitialization>,
    ) => {
      draft.initalization = action.payload;
    },
    sliceWallUpdateBackground: (
      draft,
      action: PayloadAction<SliceWallState["background"]>,
    ) => {
      draft.background = action.payload;
    },
    sliceWallUpdateLocation: (draft, action: PayloadAction<string>) => {
      draft.location = action.payload;
    },
    sliceWallUpdateProfile: (draft, action: PayloadAction<string>) => {
      draft.profile = action.payload;
    },
    sliceWallUpdateDevice: (
      draft,
      action: PayloadAction<SliceWallState["device"]>,
    ) => {
      draft.device = action.payload;
    },
    sliceWallUpdateDeviceId: (draft, action: PayloadAction<string>) => {
      draft.deviceId = action.payload;
    },
    sliceWallUpdateResting: (
      draft,
      action: PayloadAction<SliceWallState["resting"]>,
    ) => {
      draft.resting = action.payload;
    },
    sliceWallSetCaching: (
      draft,
      action: PayloadAction<SliceWallState["caching"]>,
    ) => {
      draft.caching = action.payload;
    },
    sliceWallSetStyling: (
      draft,
      action: PayloadAction<SliceWallState["styling"]>,
    ) => {
      draft.styling = action.payload;
    },
    sliceWallSetScreens: (
      draft,
      action: PayloadAction<SliceWallState["screens"]>,
    ) => {
      draft.screens = action.payload;
    },
    sliceWallSetProfileScreen: (
      draft,
      action: PayloadAction<SliceWallState["profileScreen"]>,
    ) => {
      draft.profileScreen = action.payload;
    },
    sliceWallSetType: (
      draft,
      action: PayloadAction<SliceWallState["type"]>,
    ) => {
      draft.type = action.payload;
    },
    sliceWallUpdateScreen: (
      draft,
      action: PayloadAction<{ id: string; screen: SliceWallScreenState }>,
    ) => {
      const screenIndex = draft.screens.findIndex(
        (screen) => screen.id === action.payload.id,
      );
      if (screenIndex >= 0) {
        draft.screens.splice(screenIndex, 1, action.payload.screen);
      }
    },
    sliceWallActivateScreenFlyout: (
      draft,
      action: PayloadAction<{ id: string; post: Post; relatedPosts: Post[] }>,
    ) => {
      const screen = draft.screens.find(
        (screen) => screen.id === action.payload.id,
      );
      if (screen) {
        screen.flyout = {
          post: action.payload.post,
          relatedPosts: action.payload.relatedPosts,
        };
      }
    },
    sliceWallDisableScreenFlyout: (
      draft,
      action: PayloadAction<{ id: string }>,
    ) => {
      const screen = draft.screens.find(
        (screen) => screen.id === action.payload.id,
      );
      if (screen) {
        screen.flyout = undefined;
      }
    },
    sliceWallSetServices: (
      draft,
      action: PayloadAction<SliceWallState["services"]>,
    ) => {
      draft.services = action.payload;
    },
    sliceWallOpenScreenMenu: (draft, action: PayloadAction<{ id: string }>) => {
      const screen = draft.screens.find(
        (screen) => screen.id === action.payload.id,
      );
      if (screen) {
        screen.menu = true;
      }
    },
    sliceWallCloseScreenMenu: (
      draft,
      action: PayloadAction<{ id: string }>,
    ) => {
      const screen = draft.screens.find(
        (screen) => screen.id === action.payload.id,
      );
      if (screen) {
        screen.menu = false;
      }
    },
    sliceWallUpdateScreenMenuLocation: (
      draft,
      action: PayloadAction<DeviceMenuLocation>,
    ) => {
      draft.screenMenuLocation = action.payload;
    },
    sliceWallUpdateVideoSource: (
      draft,
      action: PayloadAction<string | undefined>,
    ) => {
      draft.videoSource = action.payload;
    },
  },
});

export type ReduxWallState = {
  wall: SliceWallState;
};
type ReduxReducer = ReducersMapObject<
  ReduxWallState,
  UnknownAction,
  ReduxWallState
>;

export const {
  sliceWallActivateScreenFlyout,
  sliceWallDisableScreenFlyout,
  sliceWallResetState,
  sliceWallSetCaching,
  sliceWallSetInitialization,
  sliceWallSetScreens,
  sliceWallSetServices,
  sliceWallSetStyling,
  sliceWallSetProfileScreen,
  sliceWallSetType,
  sliceWallUpdateBackground,
  sliceWallUpdateDevice,
  sliceWallUpdateDeviceId,
  sliceWallUpdateLocation,
  sliceWallUpdateProfile,
  sliceWallUpdateResting,
  sliceWallUpdateScreen,
  sliceWallOpenScreenMenu,
  sliceWallCloseScreenMenu,
  sliceWallUpdateScreenMenuLocation,
  sliceWallUpdateVideoSource,
} = slice.actions;

export const storeReducerWall: ReduxReducer = {
  wall: slice.reducer,
};

export type ReduxWallStore = Store<ReduxWallState, UnknownAction>;
